import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { safeInternalPath } from '@/utils/safeNext';
import { AuthsApi } from '@api';
import {
  FormController,
  FormInput,
  FormPasswordInput,
  formSchemas,
} from '@components/form';
import { VStack, defineStyle } from '@chakra-ui/react';
import { toasterApi } from '@ui';
import { z } from 'zod';
import { useAppDispatch, useAppSelector } from '@state';
import {
  clearSelectedProject,
  clearSelectedProjectSheet,
  clearSelectedRelease,
} from '@/state/slices';

const formSchema = z.object({
  username: formSchemas.userSchema.usernameSchema,
  password: formSchemas.userSchema.loginPasswordSchema,
});

type FormData = z.infer<typeof formSchema>;

const Login: React.FC<{ initiateUserLogin?: boolean }> = ({
  initiateUserLogin = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const nextPath = safeInternalPath(searchParams.get('next')) ?? '/';

  const userid = useAppSelector((state) => state.auth.current?.userid);

  const [loginState, setLoginState] = useState<
    'initialised' | 'processing' | 'completed' | 'error'
  >(initiateUserLogin ? 'initialised' : 'processing');

  const hasNavigated = React.useRef(false);

  const [postAuthLogin, { isLoading }] = AuthsApi.Api.usePostAuthLoginMutation();
  const [triggerGetMe] = AuthsApi.Api.useLazyGetMeQuery();

  React.useEffect(() => {
    if (hasNavigated.current) return;

    // Only navigate once actual authenticated user exists in store
    if (userid) {
      hasNavigated.current = true;
      navigate(nextPath, { replace: true });
    }
  }, [userid, navigate, nextPath]);

  const onFormSubmit = async (formData: FormData) => {
    try {
      setLoginState('processing');

      const payload = {
        ...formData,
        username: formData.username.trim().toLowerCase(),
      };

      const loginRes = await postAuthLogin(
        payload as AuthsApi.Args['postAuthLogin'],
      ).unwrap();

      if (!loginRes?.user_id) {
        throw new Error('Login succeeded but no user id was returned.');
      }

      // Important: wait for /user/me/ to complete before considering login done
      await triggerGetMe().unwrap();

      // Properly dispatch these actions
      dispatch(clearSelectedProject());
      dispatch(clearSelectedRelease());
      dispatch(clearSelectedProjectSheet());

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Login successful',
      });

      setLoginState('completed');
      // Do NOT navigate here.
      // Navigation should happen only when userid exists in store.
    } catch (error: any) {
      setLoginState('error');

      toasterApi({
        type: 'error',
        status: error?.status ?? 500,
        code: error?.data?.code ?? 'LoginError',
        message:
          error?.data?.message ??
          error?.data?.detail ??
          'Failed to login. Please try again.',
      });
    }
  };

  const submitOnEnter: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit?.();
    }
  };

  return (
    <FormController
      label="Login"
      formSchema={formSchema}
      helperText="Please enter your credentials to login."
      onFormSubmit={onFormSubmit}
      buttonProps={{
        width: '100%',
        colorPalette: 'blue',
        loading: isLoading || loginState === 'processing',
        disabled: isLoading || loginState === 'processing',
        'data-testid': 'tutorial-login-submit',
        'data-tutorial-id': 'login-submit',
      }}
    >
      {({ register, control, errors }) => (
        <VStack gap={4}>
          <FormInput
            label="Username"
            id="username"
            type="text"
            register={register}
            control={control}
            errors={errors}
            helperText={formSchemas.userSchema.usernameHelper}
            labelProps={customFormStyle}
            data-testid="tutorial-login-email"
            data-tutorial-id="login-username"
          />
          <FormPasswordInput
            label="Password"
            id="password"
            register={register}
            control={control}
            errors={errors}
            helperText={formSchemas.userSchema.loginPasswordHelper}
            onKeyDown={submitOnEnter}
            labelProps={customFormStyle}
            data-testid="tutorial-login-password"
            data-tutorial-id="login-password"
          />
        </VStack>
      )}
    </FormController>
  );
};

export default Login;

const customFormStyle = defineStyle({
  minWidth: '100px',
});